import React from 'react';
import { defaultTheme, pxToRem } from '@withjoy/joykit/theme';
import { Box, BoxProps, Flex } from '@withjoy/joykit';
import { StyledBoxShadow, StyledBorder, StyledArrow, BORDER_RADIUS, WIDTH_HEIGHT, LEFT_RIGHT, BOX_SHADOW } from './AiBorderBox.styles';

const { colors: themeColors } = defaultTheme;

export type AiBorderBoxIntent = 'aiBorder' | 'border' | 'noBorder';

export interface AiBorderBoxProps {
  children?: React.ReactNode;
  intent?: AiBorderBoxIntent;
  background?: BoxProps['background'];
  showTransparentBackground?: boolean;
  backdropFilter?: BoxProps['backdropFilter'];
  width?: BoxProps['width'];
  padding?: BoxProps['padding'];
  borderRadius?: number;
  showShadow?: boolean;
  showGradientShadow?: boolean;
  showArrow?: boolean;
  isActive?: boolean;
}

const intentStyles: Record<
  AiBorderBoxIntent,
  {
    stroke: string;
    backgroundColor?: BoxProps['backgroundColor'];
    showGradientBorder: boolean;
  }
> = {
  aiBorder: {
    stroke: 'gradient',
    showGradientBorder: true,
  },
  border: {
    stroke: themeColors.mono10,
    backgroundColor: 'mono10',
    showGradientBorder: false,
  },
  noBorder: {
    stroke: 'white',
    backgroundColor: 'white',
    showGradientBorder: false,
  },
};

export const AiBorderBox = ({
  intent = 'aiBorder',
  background = 'white',
  showTransparentBackground,
  backdropFilter,
  width = 'max-content',
  borderRadius = BORDER_RADIUS,
  padding,
  showShadow,
  showGradientShadow,
  showArrow,
  isActive,
  children,
}: AiBorderBoxProps) => {
  const { stroke, backgroundColor, showGradientBorder } = intentStyles[intent];
  const showBoxShadow = !(showGradientBorder && showGradientShadow);
  const borderColor = isActive ? themeColors.mono14 : stroke;
  return (
    <StyledBoxShadow
      position="relative"
      width={width}
      maxWidth="100%"
      paddingX={pxToRem(20)}
      paddingY={pxToRem(10)}
      padding={padding}
      zIndex={1}
      showGradientShadow={!showBoxShadow}
      borderRadius={borderRadius}
    >
      <StyledBorder
        position="absolute"
        width={WIDTH_HEIGHT}
        height={WIDTH_HEIGHT}
        left={LEFT_RIGHT}
        top={LEFT_RIGHT}
        borderRadius={borderRadius}
        zIndex={1}
        showGradientBorder={showGradientBorder}
        backgroundColor={borderColor ?? backgroundColor}
        boxShadow={showBoxShadow && showShadow ? BOX_SHADOW : undefined}
        showTransparentBackground={showTransparentBackground}
      />

      <Box
        position="absolute"
        inset="5px"
        borderRadius={borderRadius - 2}
        pointerEvents="none"
        zIndex={3}
        background={background}
        backdropFilter={backdropFilter}
      />

      {/* Handled down arrow styles based on props, so kept here instead of separate file */}
      {showArrow && (
        <StyledArrow zIndex={2}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="13"
            viewBox="0 0 18 13"
            fill="none"
            css={{ filter: `drop-shadow(${BOX_SHADOW})` }}
          >
            {/* Fill triangle */}
            <path
              d="M9 13L1 0H17L9 13Z"
              fill="white"
            />
            {/* Gradient definition */}
            {stroke === 'gradient' && (
              <defs>
                <linearGradient
                  id="triangle-gradient-stroke"
                  x1="1"
                  y1="6.5"
                  x2="17"
                  y2="6.5"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#25AFFA" />
                  <stop
                    offset="0.543269"
                    stopColor="#4951EF"
                  />
                  <stop
                    offset="1"
                    stopColor="#CE54B3"
                  />
                </linearGradient>
              </defs>
            )}
            {/* Border lines */}
            <line
              x1="1"
              y1="0"
              x2="9"
              y2="13"
              stroke={stroke === 'gradient' ? 'url(#triangle-gradient-stroke)' : borderColor}
              strokeWidth="1.5"
            />
            <line
              x1="17"
              y1="0"
              x2="9"
              y2="13"
              stroke={stroke === 'gradient' ? 'url(#triangle-gradient-stroke)' : borderColor}
              strokeWidth="1.5"
            />
          </svg>
        </StyledArrow>
      )}

      <Flex
        position="relative"
        alignItems="center"
        gap={4}
        zIndex={4}
      >
        <Box
          justifyContent="flex-start"
          alignItems="center"
          gap={3}
          display="inline-flex"
          zIndex={4}
          width={'100%'}
        >
          {children}
        </Box>
      </Flex>
    </StyledBoxShadow>
  );
};
